import React, { useState, useCallback, useEffect, useRef } from "react";
import { IconButton, makeStyles } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  slideContainer: {
    display: "flex",
    transition: "transform 0.3s ease-in-out",
    padding: theme.spacing(2, 0),
  },
  slide: {
    flex: "0 0 100%",
    height: "auto",
    minHeight: "200px",
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navigationButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
    padding: `3px`,
  },
  prevButton: {
    left: 0,
  },
  nextButton: {
    right: 0,
  },
  dots: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    position: "relative",
    zIndex: 1,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[300],
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
}));

const JsxCarousel = ({
  slides,
  currentSlide = 0,
  updateCurrentSlide,
  activeColor = "#0a474c", // Default active color if none provided
  // Optional CSS `top` override for the prev/next arrows (e.g. "166px") —
  // lets a caller whose slides are taller than just their media (media +
  // footer text, etc.) center the arrows on the media region instead of
  // the whole slide. Defaults to vertically centering on the whole slide,
  // unchanged from before.
  arrowsTop,
  // When true, a change in slide count re-syncs the shown slide to
  // `currentSlide` via a plain state update instead of requiring the
  // caller to force a remount (e.g. `key={slides.length}`) to reset
  // `activeSlide`'s initial value — a remount tears down and rebuilds
  // every slide's DOM (including <video>/<img> elements), which is
  // measurably slower than updating state in place. Off by default so
  // callers that manage their own slide position independently of any
  // externally-driven index (ImagePreviewSlides, CreateTemplateMsgForm)
  // keep their current behavior — those never pass a meaningful
  // `currentSlide`, so syncing to it on every count change would snap
  // their view back to slide 0 whenever an item is added/removed.
  syncSlideOnCountChange = false,
}) => {
  const classes = useStyles();
  const [activeSlide, setActiveSlide] = useState(currentSlide);
  const prevSlideCount = useRef(slides.length);

  useEffect(() => {
    if (syncSlideOnCountChange && slides.length !== prevSlideCount.current) {
      setActiveSlide(Math.min(Math.max(currentSlide, 0), slides.length - 1));
    }
    prevSlideCount.current = slides.length;
  }, [slides.length, currentSlide, syncSlideOnCountChange]);

  const handleSlideChange = useCallback(
    (index) => {
      setActiveSlide(index);
      if (updateCurrentSlide) {
        updateCurrentSlide(index);
      }
    },
    [updateCurrentSlide]
  );

  const nextSlide = () => {
    if (activeSlide < slides.length - 1) {
      handleSlideChange(activeSlide + 1);
    }
  };

  const prevSlide = () => {
    if (activeSlide > 0) {
      handleSlideChange(activeSlide - 1);
    }
  };

  return (
    <div className={classes.root}>
      <div
        className={classes.slideContainer}
        style={{ transform: `translateX(-${activeSlide * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div key={index} className={classes.slide}>
            {slide}
          </div>
        ))}
      </div>

      <IconButton
        className={`${classes.navigationButton} ${classes.prevButton}`}
        style={arrowsTop ? { top: arrowsTop } : undefined}
        onClick={prevSlide}
        disabled={activeSlide === 0}
      >
        <ChevronLeft />
      </IconButton>

      <IconButton
        className={`${classes.navigationButton} ${classes.nextButton}`}
        style={arrowsTop ? { top: arrowsTop } : undefined}
        onClick={nextSlide}
        disabled={activeSlide === slides.length - 1}
      >
        <ChevronRight />
      </IconButton>

      <div className={classes.dots}>
        {slides.map((_, index) => (
          <div
            key={index}
            className={classes.dot}
            style={{
              backgroundColor: index === activeSlide ? activeColor : undefined,
            }}
            onClick={() => handleSlideChange(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default JsxCarousel;
